import { useQuery } from "@apollo/client";
import { Calendar, ChevronLeft, ChevronRight, Clock, Download, Filter, Users } from "lucide-react";
import { DateTime } from "luxon";
import React, { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { isToday } from "@/domains/appointment/appointments/utils/calendar-utils";
import { useWorkersQuery } from "@/domains/workers/hooks/use-workers-query";
import { gql } from "@/gql";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";

// GraphQL query for weekly shifts
const GET_WEEKLY_SHIFTS = gql(`
  query GetWeeklyShifts($filters: ShiftFilterInput!) {
    getShifts(filters: $filters) {
      id
      patternId
      workerSalonId
      shiftDate
      startTime
      endTime
      status
      shiftType
      notes
      replacementWorkerSalonId
      isException
      createdAt
      updatedAt
      breaks {
        id
        shiftId
        breakType
        duration
        startTime
        endTime
        isPaid
        isRequired
        isTaken
      }
    }
  }
`);

interface WeeklyCalendarViewProps {
  className?: string;
}

interface DayColumnProps {
  date: DateTime;
  workers: any[];
  shiftsByWorker: Record<string, any[]>;
  onDayClick?: (date: DateTime) => void;
}

interface WeeklyShiftCardProps {
  shift: any;
  worker: any;
  style: React.CSSProperties;
  onClick?: () => void;
}

// Individual shift card component
function WeeklyShiftCard({ shift, worker, style, onClick }: WeeklyShiftCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "SCHEDULED":
        return "bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300";
      case "CONFIRMED":
        return "bg-green-100 border-green-300 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300";
      case "IN_PROGRESS":
        return "bg-yellow-100 border-yellow-300 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-300";
      case "COMPLETED":
        return "bg-gray-100 border-gray-300 text-gray-800 dark:bg-gray-900/20 dark:border-gray-700 dark:text-gray-300";
      case "CANCELLED":
        return "bg-red-100 border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300";
      default:
        return "bg-gray-100 border-gray-300 text-gray-800 dark:bg-gray-900/20 dark:border-gray-700 dark:text-gray-300";
    }
  };

  return (
    <div
      className={cn(
        "absolute left-1 right-1 rounded border-l-4 border cursor-pointer hover:shadow-md transition-all p-1 text-xs",
        getStatusColor(shift.status)
      )}
      style={{
        ...style,
        borderLeftColor: worker.color || "#3b82f6",
        minHeight: "20px",
      }}
      onClick={onClick}
    >
      <div className="font-medium truncate">
        {shift.startTime} - {shift.endTime}
      </div>
      {shift.isException && <div className="text-xs opacity-75">Exception</div>}
    </div>
  );
}

// Day column component showing all workers for a single day
function DayColumn({ date, workers, shiftsByWorker, onDayClick }: DayColumnProps) {
  const isCurrentDay = isToday(date);
  const dayShifts = Object.values(shiftsByWorker).flat();
  const totalShifts = dayShifts.length;

  // Calculate time scale configuration
  const hourHeight = 60; // pixels per hour
  const startHour = 6; // 6 AM
  const endHour = 22; // 10 PM
  const totalHours = endHour - startHour;

  return (
    <div className="flex-1 border-r border-border last:border-r-0 min-w-0">
      {/* Day header */}
      <div
        className={cn(
          "h-16 border-b border-border flex flex-col items-center justify-center px-1 cursor-pointer hover:bg-muted/50 transition-colors min-w-0",
          isCurrentDay && "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800"
        )}
        onClick={() => onDayClick?.(date)}
      >
        <div
          className={cn(
            "text-xs font-medium text-muted-foreground truncate",
            isCurrentDay && "text-blue-700 dark:text-blue-300"
          )}
        >
          <span className="hidden sm:inline">{date.toFormat("EEE")}</span>
          <span className="sm:hidden">{date.toFormat("E")}</span>
        </div>
        <div
          className={cn("text-lg font-bold", isCurrentDay && "text-blue-700 dark:text-blue-300")}
        >
          {date.day}
        </div>
        {totalShifts > 0 && (
          <Badge variant={isCurrentDay ? "default" : "secondary"} className="h-4 px-1 text-xs mt-1">
            {totalShifts}
          </Badge>
        )}
      </div>

      {/* Worker labels row */}
      <div className="h-8 border-b border-border flex bg-muted/10 relative">
        {workers.map((worker, workerIndex) => {
          const laneWidth = `${100 / workers.length}%`;
          const laneLeft = `${(workerIndex * 100) / workers.length}%`;
          const workerColor = worker.color || "#6b7280";

          return (
            <div
              key={worker.id}
              className="absolute top-0 h-8 border-r border-border/30 last:border-r-0 flex items-center justify-center px-1"
              style={{
                left: laneLeft,
                width: laneWidth,
              }}
            >
              <div className="flex items-center gap-1 min-w-0 w-full justify-center">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: workerColor }}
                />
                <span className="text-xs font-medium truncate text-center">
                  <span className="hidden lg:inline">
                    {`${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim()}
                  </span>
                  <span className="hidden sm:inline lg:hidden">
                    {`${worker.user?.firstName || ""}`}
                  </span>
                  <span className="sm:hidden">
                    {`${worker.user?.firstName?.[0] || ""}${worker.user?.lastName?.[0] || ""}`}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Time slots with shifts */}
      <div className="relative" style={{ height: `${totalHours * hourHeight}px` }}>
        {/* Hour grid lines */}
        {Array.from({ length: totalHours + 1 }).map((_, index) => (
          <div
            key={index}
            className="absolute left-0 right-0 border-b border-border/30 pointer-events-none"
            style={{ top: `${index * hourHeight}px` }}
          />
        ))}

        {/* Worker lanes and shifts */}
        {workers.map((worker, workerIndex) => {
          const workerShifts = shiftsByWorker[worker.id] || [];
          const laneWidth = `${100 / workers.length}%`;
          const laneLeft = `${(workerIndex * 100) / workers.length}%`;
          const workerColor = worker.color || "#6b7280";

          return (
            <div
              key={worker.id}
              className="absolute top-0 bottom-0 border-r border-border/30 last:border-r-0 hover:bg-muted/20 transition-colors"
              style={{
                left: laneLeft,
                width: laneWidth,
                borderRightColor: `${workerColor}20`,
              }}
              title={`${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim()}
            >
              {/* Worker shifts */}
              {workerShifts.map((shift, shiftIndex) => {
                const [shiftStartHour, shiftStartMinute] = shift.startTime.split(":").map(Number);
                const [shiftEndHour, shiftEndMinute] = shift.endTime.split(":").map(Number);

                // Calculate position relative to start hour (6 AM)
                const top = (shiftStartHour - startHour + shiftStartMinute / 60) * hourHeight;
                const durationHours =
                  shiftEndHour - shiftStartHour + (shiftEndMinute - shiftStartMinute) / 60;
                const height = Math.max(durationHours * hourHeight, 20);

                return (
                  <WeeklyShiftCard
                    key={`${shift.id}-${shiftIndex}`}
                    shift={shift}
                    worker={worker}
                    style={{
                      top: `${top}px`,
                      height: `${height}px`,
                      zIndex: 10,
                    }}
                    onClick={() => {}}
                  />
                );
              })}

              {/* Worker lane indicator */}
              <div
                className="absolute top-0 left-0 w-1 h-full opacity-30"
                style={{ backgroundColor: workerColor }}
              />

              {/* Empty state for workers with no shifts */}
              {workerShifts.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground/30">
                  <div className="text-center p-2">
                    <div>No shifts</div>
                    <div className="text-xs mt-1 opacity-60">
                      for {worker.user?.firstName || "worker"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Time axis component
function TimeAxis() {
  const startHour = 6; // 6 AM
  const endHour = 22; // 10 PM
  const hourHeight = 60;

  return (
    <div className="w-12 sm:w-14 lg:w-16 border-r border-border bg-muted/10 flex-shrink-0">
      {/* Header spacer for day header */}
      <div className="h-16 border-b border-border" />

      {/* Header spacer for worker labels */}
      <div className="h-8 border-b border-border bg-muted/10" />

      {/* Time labels */}
      <div className="relative" style={{ height: `${(endHour - startHour) * hourHeight}px` }}>
        {Array.from({ length: endHour - startHour + 1 }).map((_, index) => {
          const hour = startHour + index;
          const displayTime =
            hour === 0
              ? "12 AM"
              : hour === 12
                ? "12 PM"
                : hour > 12
                  ? `${hour - 12} PM`
                  : `${hour} AM`;

          return (
            <div
              key={hour}
              className="absolute left-0 right-0 flex items-center justify-center text-xs text-muted-foreground -translate-y-2"
              style={{ top: `${index * hourHeight}px` }}
            >
              <span className="hidden sm:inline">{displayTime}</span>
              <span className="sm:hidden">
                {hour === 12
                  ? "12P"
                  : hour > 12
                    ? `${hour - 12}P`
                    : hour === 0
                      ? "12A"
                      : `${hour}A`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Week navigation header
function WeekHeader({
  currentWeek,
  onPreviousWeek,
  onNextWeek,
  onToday,
}: {
  currentWeek: DateTime;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
}) {
  const weekStart = currentWeek.startOf("week");
  const weekEnd = currentWeek.endOf("week");

  return (
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-2xl font-bold">
          Week of {weekStart.toFormat("MMM d")} - {weekEnd.toFormat("MMM d, yyyy")}
        </h2>
        <p className="text-sm text-muted-foreground">
          Weekly shift overview with worker assignments
        </p>
      </div>

      <div className="flex items-center gap-2">
        {/* Export and filter options */}
        <Button variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Export Week
        </Button>
        <Button variant="outline" size="sm">
          <Filter className="w-4 h-4 mr-2" />
          Filter
        </Button>

        {/* Navigation */}
        <div className="flex items-center gap-1 ml-2">
          <Button variant="outline" size="sm" onClick={onPreviousWeek}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday}>
            This Week
          </Button>
          <Button variant="outline" size="sm" onClick={onNextWeek}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main weekly calendar component
export function WeeklyCalendarView({ className = "" }: WeeklyCalendarViewProps) {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [currentWeek, setCurrentWeek] = useState(() => DateTime.now().startOf("week"));

  // Fetch workers
  const { data: workersData, loading: workersLoading } = useWorkersQuery({
    salonId: currentSalon?.id,
  });

  // Generate week days
  const weekDays = useMemo(() => {
    const days: DateTime[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(currentWeek.plus({ days: i }));
    }
    return days;
  }, [currentWeek]);

  // Fetch shifts for the entire week
  const { data: shiftsData, loading: shiftsLoading } = useQuery(GET_WEEKLY_SHIFTS, {
    variables: {
      filters: {
        franchiseId: currentSalon?.franchiseId,
        startDate: currentWeek.startOf("week").toISODate(),
        endDate: currentWeek.endOf("week").toISODate(),
      },
    },
    skip: !currentSalon?.franchiseId,
  });

  // Process workers
  const processedWorkers = useMemo(() => {
    const workers = workersData?.workers?.workers || [];
    return workers.filter((worker) => worker.isActive);
  }, [workersData]);

  // Process shifts and organize by worker and date
  const shiftsByWorkerAndDate = useMemo(() => {
    if (!shiftsData?.getShifts) return {};

    const shifts = shiftsData.getShifts;
    const result: Record<string, Record<string, any[]>> = {};

    // Create mapping of workerSalonId to workerId
    const workerSalonToWorkerMap: Record<string, string> = {};
    processedWorkers.forEach((worker) => {
      result[worker.id] = {};
      // Initialize each day for each worker
      weekDays.forEach((date) => {
        result[worker.id][date.toISODate() ?? ""] = [];
      });
      // Map workerSalon relationships
      worker.workerSalons?.forEach((workerSalon) => {
        workerSalonToWorkerMap[workerSalon.id] = worker.id;
      });
    });

    shifts.forEach((shift) => {
      const workerId = workerSalonToWorkerMap[shift.workerSalonId];
      const shiftDate = DateTime.fromISO(shift.shiftDate).toISODate() ?? "";

      if (workerId && result[workerId] && result[workerId][shiftDate]) {
        result[workerId][shiftDate].push(shift);
      }
    });

    return result;
  }, [shiftsData, processedWorkers, weekDays]);

  const handlePreviousWeek = () => {
    setCurrentWeek((prev) => prev.minus({ weeks: 1 }));
  };

  const handleNextWeek = () => {
    setCurrentWeek((prev) => prev.plus({ weeks: 1 }));
  };

  const handleToday = () => {
    setCurrentWeek(DateTime.now().startOf("week"));
  };

  const handleDayClick = (_date: DateTime) => {
    // TODO: Navigate to daily view for selected date
  };

  if (!currentSalon) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Please select a salon to view weekly calendar</p>
        </CardContent>
      </Card>
    );
  }

  if (shiftsLoading || workersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading weekly calendar...</div>
      </div>
    );
  }

  if (processedWorkers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-muted-foreground mb-2">No active workers found</div>
          <div className="text-sm text-muted-foreground">
            Make sure workers are assigned to this salon and are active.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <WeekHeader
        currentWeek={currentWeek}
        onPreviousWeek={handlePreviousWeek}
        onNextWeek={handleNextWeek}
        onToday={handleToday}
      />

      <Card>
        <CardContent className="p-0">
          {/* Calendar grid with proper layout - responsive with scroll fallback on very small screens */}
          <div className="min-w-fit">
            <div className="flex">
              {/* Time axis */}
              <TimeAxis />

              {/* Week grid - responsive columns */}
              <div className="flex flex-1 min-w-0">
                {weekDays.map((date) => {
                  // Get shifts for this day across all workers
                  const dayShiftsByWorker: Record<string, any[]> = {};
                  processedWorkers.forEach((worker) => {
                    dayShiftsByWorker[worker.id] =
                      shiftsByWorkerAndDate[worker.id]?.[date.toISODate() ?? ""] || [];
                  });

                  return (
                    <DayColumn
                      key={date.toISODate()}
                      date={date}
                      workers={processedWorkers}
                      shiftsByWorker={dayShiftsByWorker}
                      onDayClick={handleDayClick}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Week summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Week Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" />
              <span className="text-sm">Total Shifts: {shiftsData?.getShifts?.length || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-green-500" />
              <span className="text-sm">Active Workers: {processedWorkers.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="text-sm">
                Avg. Shifts/Day: {Math.round(((shiftsData?.getShifts?.length || 0) / 7) * 10) / 10}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="h-5">
                Week {currentWeek.weekNumber}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
